import React from 'react';
import Webcam from 'react-webcam';
import { getDownloadURL } from 'firebase/storage';
import { PhotoCameraProps, FileUpload } from '@usacsmideo/types';
import { uploadFile } from '../data/uploadFile';
import { dataURItoBlob } from '../data/dataURItoBlob';
import { useHandleFileUploadSuccess } from '../hooks/useHandleFileUploadSuccess';
import { useMeasure, useOrientation } from '@uidotdev/usehooks';
import { Button } from './buttons';
import * as Sentry from '@sentry/browser';
// Aliased to avoid the import/no-named-as-default lint warning: react-qr-code also exports
// a named `QRCode`, so binding the default under a distinct local name keeps lint clean.
import QrCode from 'react-qr-code';

// Physical card dimensions: 3.370" x 2.125" (standard insurance/ID card)
const CARD_ASPECT_RATIO = {
  landscape: 3.37 / 2.125, // ~1.586 (wider than tall)
  portrait: 2.125 / 3.37, // ~0.6306 (taller than wide)
};

// How much of the preview area the capture box should fill (max dimension)
const CARD_BOX_FILL = 0.8;

const LAYOUT_CONFIG = {
  aspectRatios: {
    document: 1.294, // Letter size
    portrait: 0.85,
  },
  resolution: {
    portrait: { width: 2160, height: 3840 },
    landscape: { width: 3840, height: 2160 },
  },
};

// Classifies a camera-initialization failure so the UI can show cause-specific recovery
// guidance. Permission denials are split by WHERE the block lives, because a browser/OS
// NotAllowedError is identical in both cases but the fix differs:
//   - permission-browser: the site is blocked in the browser (e.g. Chrome "Never allow")
//     → fix is the address-bar site permission, NOT the OS settings.
//   - permission-os: the OS/system camera is off (site permission may be fine)
//     → fix is Windows/Mac privacy settings.
//   - permission: a denial we couldn't attribute to a layer (Permissions API unavailable)
//     → show a combined message covering both.
// Everything non-permission keeps neutral messaging so we don't claim a cause we can't confirm.
type CameraErrorKind =
  | 'permission-browser'
  | 'permission-os'
  | 'permission'
  | 'no-device'
  | 'unsupported'
  | 'generic';

interface CameraError {
  kind: CameraErrorKind;
  message: string;
}

// The site-level camera permission state, queried independently of the thrown error. This
// is what lets us tell a browser-site block from an OS block: Chrome "Never allow" reports
// 'denied' here, whereas an OS-level block leaves the SITE permission 'granted'/'prompt'.
// Returns null when the Permissions API (or the camera descriptor) is unavailable — e.g.
// older Safari/Firefox — in which case we fall back to the undifferentiated 'permission'.
const querySitePermissionState = async (): Promise<PermissionState | null> => {
  try {
    if (!navigator.permissions?.query) return null;
    const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
    return status.state;
  } catch {
    // Some engines throw for an unsupported descriptor name ('camera'). Treat as unknown.
    return null;
  }
};

// Prefer err.name over message-string matching so wording/locale changes across browsers
// don't break classification. `PermissionDeniedError`/`DevicesNotFoundError` are legacy
// Chromium aliases. Note: a DOMException does NOT reliably extend Error (e.g. jsdom, and
// some engines), so it is checked first and its message is always preserved on fallthrough.
//
// `sitePermission` is the Permissions API result (see querySitePermissionState). It only
// affects permission denials: 'denied' → browser-site block; anything else → OS block;
// null (unknown) → undifferentiated combined message.
const classifyCameraError = (err: unknown, sitePermission: PermissionState | null): CameraError => {
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      if (sitePermission === 'denied') {
        return { kind: 'permission-browser', message: err.message };
      }
      if (sitePermission === 'granted' || sitePermission === 'prompt') {
        return { kind: 'permission-os', message: err.message };
      }
      return { kind: 'permission', message: err.message };
    }
    if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
      return { kind: 'no-device', message: err.message };
    }
    // Other DOMExceptions (NotReadableError, OverconstrainedError, etc.) — keep the
    // browser-provided message rather than dropping to the opaque generic fallback.
    return { kind: 'generic', message: err.message };
  }
  if (err instanceof Error) {
    // Plain Errors thrown by initializeCamera() for the non-DOMException branches.
    if (err.message === 'Camera access not supported in this browser') {
      return { kind: 'unsupported', message: err.message };
    }
    if (err.message === 'No camera devices found') {
      return { kind: 'no-device', message: err.message };
    }
    return { kind: 'generic', message: err.message };
  }
  return { kind: 'generic', message: 'Unable to access camera' };
};

// CUSTOM HOOK FOR CAMERA DEVICE MANAGEMENT
const useCameraDevice = (preferredMode: 'front' | 'back') => {
  const [device, setDevice] = React.useState<MediaDeviceInfo | null>(null);
  const [error, setError] = React.useState<CameraError | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let isMounted = true;

    const selectDevice = (devices: MediaDeviceInfo[]): MediaDeviceInfo | null => {
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      if (videoDevices.length === 0) return null;

      // Try to find device based on preferred mode
      const keyword = preferredMode === 'front' ? 'front' : 'back';
      const preferredDevice = videoDevices.find((d) => d.label.toLowerCase().includes(keyword));

      return preferredDevice || videoDevices[0]; // Fallback to first available
    };

    const initializeCamera = async () => {
      try {
        if (!navigator.mediaDevices?.enumerateDevices) {
          throw new Error('Camera access not supported in this browser');
        }

        // Get initial device list
        let devices = await navigator.mediaDevices.enumerateDevices();

        // Check if we need to request permissions first
        const hasLabels = devices.some((d) => d.kind === 'videoinput' && d.label !== '');

        if (!hasLabels) {
          // Request permissions
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach((track) => track.stop()); // Release immediately
          // Re-enumerate to get device labels
          devices = await navigator.mediaDevices.enumerateDevices();
        }

        const selectedDevice = selectDevice(devices);

        if (!selectedDevice) {
          throw new Error('No camera devices found');
        }

        if (isMounted) {
          setDevice(selectedDevice);
          setIsLoading(false);
        }
      } catch (err) {
        // Capture the raw error (preserves name/stack for Sentry). The UI-facing state
        // carries a classified kind so we can show cause-specific recovery guidance.
        Sentry.captureException(err);
        console.error('Camera initialization error:', err);

        // Query the site-level permission state to distinguish a browser-site block from an
        // OS block (both throw an identical NotAllowedError). Best-effort; null if unknown.
        const sitePermission = await querySitePermissionState();

        if (isMounted) {
          setError(classifyCameraError(err, sitePermission));
          setIsLoading(false);
        }
      }
    };

    initializeCamera();

    return () => {
      isMounted = false;
    };
  }, [preferredMode]);

  return { device, error, isLoading };
};

// Fit a rectangle with the card's physical aspect ratio inside the preview container, then center it.
// Always uses landscape card ratio — the physical card is the same shape regardless of device orientation.
const getCaptureBoxStyles = (
  containerWidth: number | null,
  containerHeight: number | null,
): React.CSSProperties => {
  if (!containerWidth || !containerHeight) return {};

  const targetAspectRatio = CARD_ASPECT_RATIO.landscape;

  // Available space after applying fill factor
  const maxWidth = containerWidth * CARD_BOX_FILL;
  const maxHeight = containerHeight * CARD_BOX_FILL;

  // Fit the card aspect ratio within available space
  let boxWidth: number;
  let boxHeight: number;

  if (maxWidth / maxHeight > targetAspectRatio) {
    // Container is wider than needed — constrain by height
    boxHeight = maxHeight;
    boxWidth = boxHeight * targetAspectRatio;
  } else {
    // Container is taller than needed — constrain by width
    boxWidth = maxWidth;
    boxHeight = boxWidth / targetAspectRatio;
  }

  return {
    width: `${boxWidth}px`,
    height: `${boxHeight}px`,
    top: `${(containerHeight - boxHeight) / 2}px`,
    left: `${(containerWidth - boxWidth) / 2}px`,
    outline: '5px solid #fff',
    position: 'absolute',
    borderRadius: '15px',
  };
};

// HELPER TO GET VIDEO CONSTRAINTS
const getVideoConstraints = (photoType: 'card' | 'portrait' | 'document', orientation: string, deviceId?: string) => {
  const isPortrait = orientation.includes('portrait');

  // For card type, use high resolution + always hint landscape aspect ratio (physical card is always landscape)
  if (photoType === 'card') {
    const resolution = isPortrait ? LAYOUT_CONFIG.resolution.portrait : LAYOUT_CONFIG.resolution.landscape;
    return {
      width: { ideal: resolution.width },
      height: { ideal: resolution.height },
      aspectRatio: { ideal: CARD_ASPECT_RATIO.landscape },
      deviceId,
    };
  }

  // For other types, use aspect ratio
  const aspectRatio =
    photoType === 'document' ? LAYOUT_CONFIG.aspectRatios.document : LAYOUT_CONFIG.aspectRatios.portrait;

  return {
    aspectRatio,
    deviceId,
  };
};

export const PhotoCamera2 = (props: PhotoCameraProps) => {
  const { working, setWorking, style, hideCamera } = props;
  const [outlineRef, { width, height }] = useMeasure();
  const orientation = useOrientation();
  const webcamRef = React.useRef<Webcam>(null);
  const { updateFirestore } = useHandleFileUploadSuccess();

  // Determine camera mode based on photo type
  const cameraMode = style === 'portrait' ? 'front' : 'back';
  const { device, error: cameraError, isLoading } = useCameraDevice(cameraMode);

  // Handle file upload
  const handleImageUpload = React.useCallback(
    (imageSrc: string) => {
      const file = dataURItoBlob(imageSrc);
      // const file = new File([blob], "photo.jpg", { type: blob.type, lastModified: Date.now() });
      const payload: FileUpload = {
        bucket: props.bucket,
        path: props.path,
        file,
      };

      const uploadTask = uploadFile(payload);

      if (!uploadTask) {
        Sentry.captureMessage('Unable to create upload task');
        console.error('Unable to create upload task');
        hideCamera();
        alert(
          'Unable to connect to upload server. This is likely due to your current network quality or configuration. Please try again from your personal network without a VPN. If this problem persists, please notify support.',
        );
        return;
      }

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.info(`Upload is ${progress.toFixed(0)}% done`);
        },
        (error) => {
          Sentry.captureException(error);
          console.error('Upload error:', error);
          setWorking(false);
          alert(`Error uploading file: ${error.message}`);
        },
        () => {
          // Upload successful
          getDownloadURL(uploadTask.snapshot.ref)
            .then((downloadURL) => {
              const payload = {
                docToUpdate: props.docToUpdate,
                patientId: props.patientId,
                type: props.type,
                url: downloadURL,
              };
              updateFirestore(payload);
              hideCamera();
            })
            .catch((error) => {
              Sentry.captureException(error);
              console.error('Error getting download URL:', error);
              setWorking(false);
              alert('Failed to save photo. Please try again.');
            });
        },
      );
    },
    [props, hideCamera, updateFirestore, setWorking],
  );

  // Capture photo
  const capture = React.useCallback(() => {
    if (!webcamRef.current) return;

    setWorking(true);
    const imageSrc = webcamRef.current.getScreenshot();

    if (!imageSrc) {
      setWorking(false);
      alert('Failed to capture image. Please try again.');
      return;
    }

    handleImageUpload(imageSrc);
  }, [handleImageUpload, setWorking]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <p className="text-lg">Accessing camera...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (cameraError || !device) {
    const kind = cameraError?.kind;
    const isPermissionDenied = kind === 'permission-browser' || kind === 'permission-os' || kind === 'permission';
    // Continue-on-mobile recovery target: the exact screen the patient is already on, so
    // scanning resumes this same flow on their phone. Built from the configured portal base
    // URL + the current path (falls back to the live href) so it stays correct across all
    // routes that use this shared camera, not just govId.
    const baseUrl = import.meta.env.VITE_PORTAL_BASE_URL as string | undefined;
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const mobileContinueURL = baseUrl
      ? `${baseUrl}${currentPath}`
      : typeof window !== 'undefined'
        ? window.location.href
        : '';

    return (
      // Natural top-aligned flow (no min-h-screen): the error sits directly under the
      // section header instead of being vertically centered in the viewport, which left a
      // large white gap on tall screens.
      <div className="mx-auto max-w-md px-4 py-6 text-center">
        {isPermissionDenied ? (
          <>
            <p id="camera-permission-denied-message" className="mb-3 text-lg font-bold text-red-600">
              Camera access is blocked
            </p>

            {/* Cause-specific fix. Each variant leads with the single action that resolves
                THAT block, so a patient isn't sent to OS settings when the browser is the
                culprit (or vice-versa). */}
            {kind === 'permission-browser' && (
              <p id="camera-fix-browser" className="mb-4 text-base">
                This website is blocked from using your camera. To fix it, click the{' '}
                <span className="font-semibold">camera icon</span> (or the lock/tune icon) at the{' '}
                <span className="font-semibold">left end of your browser&apos;s address bar</span>, choose{' '}
                <span className="font-semibold">Allow</span>, then reload this page and tap{' '}
                <span className="font-semibold">Take Photo</span> again.
              </p>
            )}
            {kind === 'permission-os' && (
              <p id="camera-fix-os" className="mb-4 text-base">
                Your computer&apos;s camera is turned off for apps. On Windows, open{' '}
                <span className="font-semibold">Settings → Privacy &amp; security → Camera</span> and turn on{' '}
                <span className="font-semibold">Camera access</span> and{' '}
                <span className="font-semibold">Let apps access your camera</span>. On a Mac, open{' '}
                <span className="font-semibold">System Settings → Privacy &amp; Security → Camera</span> and enable your
                browser. Then reload this page.
              </p>
            )}
            {kind === 'permission' && (
              <p id="camera-fix-generic" className="mb-4 text-base">
                Your browser or computer is blocking the camera. First, click the{' '}
                <span className="font-semibold">camera icon at the left of your browser&apos;s address bar</span> and
                choose <span className="font-semibold">Allow</span>. If that doesn&apos;t help, check your computer&apos;s
                camera privacy settings (Windows:{' '}
                <span className="font-semibold">Settings → Privacy &amp; security → Camera</span>; Mac:{' '}
                <span className="font-semibold">System Settings → Privacy &amp; Security → Camera</span>). Then reload
                this page.
              </p>
            )}

            {/* Mobile-QR recovery — promoted as the easy escape hatch for patients who don't
                want to change settings. Boxed so it reads as a distinct "or just do this". */}
            {mobileContinueURL && (
              <div
                id="camera-permission-denied-mobile-fallback"
                className="mx-auto mb-4 flex max-w-sm flex-col items-center rounded-lg border-2 border-gray-200 bg-gray-50 p-4"
              >
                <p className="mb-1 text-base font-bold">Easiest option: use your phone</p>
                <p className="mb-3 text-sm">Scan this code with your phone&apos;s camera to finish this step there — no settings to change.</p>
                <QrCode
                  id="camera-permission-denied-qr"
                  size={200}
                  style={{ height: 'auto', maxWidth: '70%', width: '100%' }}
                  value={mobileContinueURL}
                  viewBox="0 0 256 256"
                />
              </div>
            )}
          </>
        ) : (
          <p className="mb-4 text-lg text-red-600">
            {cameraError?.message || 'Unable to access camera. Please check your device settings and permissions.'}
          </p>
        )}
        <Button type="button" id="close-camera" textContent="Close" onClick={hideCamera} working={false} />
      </div>
    );
  }

  const videoConstraints = getVideoConstraints(style, orientation.type, device.deviceId);
  const captureBoxStyles = style === 'card' ? getCaptureBoxStyles(width, height) : undefined;

  const isPortrait = orientation.type.includes('portrait');

  // Only constrain the preview container aspect ratio in landscape (desktop fix for 4:3 cameras).
  // In portrait (mobile), let the camera stream size the container naturally.
  // Max height leaves room for the capture button below the preview.
  const cardContainerStyle: React.CSSProperties | undefined =
    style === 'card' && !isPortrait
      ? {
          aspectRatio: CARD_ASPECT_RATIO.landscape,
          overflow: 'hidden',
          maxHeight: '70vh',
        }
      : undefined;

  return (
    <div className="flex flex-col items-center">
      <div id="photo-camera-area" ref={outlineRef} className="relative w-full" style={cardContainerStyle}>
        {/* Card capture guide overlay */}
        {style === 'card' && captureBoxStyles && <div id="photo-camera-card-safe-outline" style={captureBoxStyles} />}

        <Webcam
          id="photo-camera"
          className={style === 'card' && !isPortrait ? 'h-full w-full object-cover' : ''}
          audio={false}
          ref={webcamRef}
          videoConstraints={videoConstraints}
          screenshotFormat="image/jpeg"
          screenshotQuality={1}
          forceScreenshotSourceSize={true}
        />
      </div>

      <Button
        id="take-photo"
        type="button"
        textContent="Capture photo"
        onClick={capture}
        working={working}
        workingText="Capturing..."
        disabled={working}
      />
    </div>
  );
};
